// aiTokenBudget — pre-flight-check som varnar user när extend-mode-
// payload närmar sig modellens context-window. Förhindrar att AI:n
// silent-trunkar sin output (vanlig orsak till "blocks försvann"-buggar).
//
// Konstanterna är konservativa estimat — verkliga gränser från LOVABLE
// gateway / OpenAI / Gemini varierar. Vi mäter i 4-char-per-token-
// approximation (engelsk text); för svensk text och JSON är ratio:n
// snarare 3.5-3.8 chars/token, så vår räkning är något pessimistisk
// (= early-warning, vilket vi vill).

import type { MultiSheetWorkflowState, WorkflowState } from '@/types/workflow';

const CHARS_PER_TOKEN = 4;
const SYSTEM_PROMPT_OVERHEAD_TOKENS = 3000; // schema + few-shot + extend-guard
const COMPLETION_RESERVATION_TOKENS = 8000; // utrymme AI:n behöver för output

export interface ModelBudget {
  /** Total context-window. */
  maxTokens: number;
  /** Hård cap på single-message-output. */
  maxOutputTokens?: number;
}

const MODEL_BUDGETS: Record<string, ModelBudget> = {
  'google/gemini-3-flash-preview':  { maxTokens: 1_048_576, maxOutputTokens: 65_536 },
  'google/gemini-2.5-flash':        { maxTokens: 1_048_576, maxOutputTokens: 65_536 },
  'google/gemini-2.5-flash-lite':   { maxTokens: 1_048_576, maxOutputTokens: 65_536 },
  'google/gemini-2.5-pro':          { maxTokens: 2_097_152, maxOutputTokens: 65_536 },
  'openai/gpt-5':                   { maxTokens: 200_000,   maxOutputTokens: 16_384 },
  'openai/gpt-5-mini':              { maxTokens: 128_000,   maxOutputTokens: 16_384 },
  'openai/gpt-5-nano':              { maxTokens: 128_000,   maxOutputTokens: 16_384 },
};
const DEFAULT_BUDGET: ModelBudget = { maxTokens: 128_000, maxOutputTokens: 16_384 };

export interface TokenBudgetCheck {
  /** Estimat på prompt-tokens (snapshot + persona + conversation). */
  estimatedPromptTokens: number;
  /** Hur stor andel av context-window vi förbrukar (0-1). */
  utilization: number;
  /** Tier: 'ok' < 0.6, 'warn' 0.6-0.85, 'critical' > 0.85. */
  tier: 'ok' | 'warn' | 'critical';
  /** Modell-budget vi jämfört mot. */
  budget: ModelBudget;
}

function estimateChars(snapshot: WorkflowState | MultiSheetWorkflowState | undefined): number {
  if (!snapshot) return 0;
  try {
    return JSON.stringify(snapshot).length;
  } catch {
    return 0;
  }
}

function estimateConversationChars(conversation: Array<{ content?: string }>): number {
  return conversation.reduce((n, m) => n + (typeof m?.content === 'string' ? m.content.length : 0), 0);
}

/**
 * Estimera prompt-tokens och returnera tier ('ok' / 'warn' / 'critical').
 * Caller renderar varning vid 'warn' och blockerar/föreslår alternativ
 * vid 'critical'.
 */
export function checkTokenBudget(args: {
  snapshot?: WorkflowState | MultiSheetWorkflowState;
  conversation?: Array<{ content?: string }>;
  honingPromptChars?: number;
  model: string;
}): TokenBudgetCheck {
  const budget = MODEL_BUDGETS[args.model] ?? DEFAULT_BUDGET;
  const snapshotChars = estimateChars(args.snapshot);
  const convoChars = estimateConversationChars(args.conversation ?? []);
  const honingChars = args.honingPromptChars ?? 0;
  const estimatedPromptTokens =
    SYSTEM_PROMPT_OVERHEAD_TOKENS +
    Math.ceil((snapshotChars + convoChars + honingChars) / CHARS_PER_TOKEN);
  const usable = budget.maxTokens - COMPLETION_RESERVATION_TOKENS;
  const utilization = estimatedPromptTokens / Math.max(usable, 1);
  const tier: TokenBudgetCheck['tier'] =
    utilization > 0.85 ? 'critical'
    : utilization > 0.6 ? 'warn'
    : 'ok';
  return { estimatedPromptTokens, utilization, tier, budget };
}
